import { useState, useMemo } from 'react'
import { Clock, Info, Check, ArrowLeft, ArrowRight, FileText, Download, AlertTriangle, Calendar, TrendingUp } from 'lucide-react'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, formatDate } from '../hooks/useSupabase'
import { supabase } from '../lib/supabase'
import { Button, Card, Input, Select, PageHeader, Toast, StatCard, Badge, Modal } from '../components/ui/SharedUI'

const PROGRAMS = [
  { value: '', label: 'Select program...' },
  { value: 'icwp', label: 'ICWP — Independent Care Waiver Program' },
  { value: 'ccsp', label: 'CCSP — Community Care Services Program' },
  { value: 'source', label: 'SOURCE — Service Options Using Resources in a Community Environment' },
  { value: 'edwp', label: 'EDWP — Elderly & Disabled Waiver Program' },
  { value: 'sfc', label: 'SFC — Structured Family Caregiving' },
]

const PROGRAM_DESCRIPTIONS = {
  icwp: 'For adults ages 21+ with severe physical disabilities who need nursing facility-level care but want to live at home.',
  ccsp: 'For adults ages 65+ or with disabilities who qualify for nursing home placement but choose home and community-based services.',
  source: 'A primary care case management program for adults with disabilities or older adults who qualify for a nursing home level of care.',
  edwp: 'Combined waiver providing services to older adults and people with disabilities to remain in the community.',
  sfc: 'Provides support and financial assistance to family caregivers who provide daily care to a loved one with a disability.',
}

export function WaitingPeriodPage() {
  const { profile, refreshProfile } = useAuth()

  if (!profile) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  return profile.in_waiting_period
    ? <WaitingPeriodDashboard />
    : <ActivationWizard onActivate={refreshProfile} />
}

// ═══════════════════════════════════════════════════════
// ACTIVATION WIZARD
// ═══════════════════════════════════════════════════════

function ActivationWizard({ onActivate }) {
  const { user } = useAuth()
  const [step, setStep] = useState(1)
  const [applicationDate, setApplicationDate] = useState('')
  const [program, setProgram] = useState('')
  const [toast, setToast] = useState(null)
  const [activating, setActivating] = useState(false)

  async function handleActivate() {
    setActivating(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          in_waiting_period: true,
          wp_application_date: applicationDate,
          wp_program: program,
        })
        .eq('id', user.id)
      if (error) throw error
      onActivate()
      setToast({ message: 'Waiting Period Mode activated', type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
      setActivating(false)
    }
  }

  const canProceed = {
    1: true,
    2: !!applicationDate,
    3: !!program,
    4: !!applicationDate && !!program,
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '720px' }}>
      <PageHeader
        title="Waiting Period Mode"
        description="Document care you're providing while your Medicaid waiver application is pending."
      />

      {/* Progress indicator */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '1.5rem' }}>
        {[1, 2, 3, 4].map(n => (
          <div key={n} style={{
            flex: 1, height: '4px', borderRadius: '2px',
            background: n <= step ? '#2C7A7B' : '#E2E8F0',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>

      <Card style={{ padding: '2rem' }}>
        {step === 1 && <Step1Intro />}
        {step === 2 && <Step2Date value={applicationDate} onChange={setApplicationDate} />}
        {step === 3 && <Step3Program value={program} onChange={setProgram} />}
        {step === 4 && <Step4Confirm applicationDate={applicationDate} program={program} />}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #EDF2F7',
        }}>
          {step > 1 ? (
            <Button variant="secondary" onClick={() => setStep(step - 1)}>
              <ArrowLeft size={14} /> Back
            </Button>
          ) : <div />}

          <div style={{ fontSize: '13px', color: '#718096' }}>Step {step} of 4</div>

          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed[step]}>
              Continue <ArrowRight size={14} />
            </Button>
          ) : (
            <Button onClick={handleActivate} disabled={!canProceed[4] || activating}>
              {activating ? 'Activating...' : 'Activate Waiting Period Mode'}
            </Button>
          )}
        </div>
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}

function Step1Intro() {
  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div style={{ padding: '10px', background: '#FCF5E8', borderRadius: '10px' }}>
          <Clock size={22} style={{ color: '#8b5000' }} />
        </div>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '22px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
            What is Waiting Period Mode?
          </h2>
          <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.7 }}>
            Georgia Medicaid waiver applications can take weeks or months to approve. During that time, family caregivers often continue providing full care without any documentation of their work. Waiting Period Mode helps you document that pre-approval care in case it becomes relevant to your compensation start date or a retroactive claim.
          </p>
        </div>
      </div>

      <div style={{ background: '#F7FAFC', padding: '1rem 1.25rem', borderRadius: '10px', marginBottom: '1rem' }}>
        <h3 style={sectionTitleStyle}>What activating this mode does</h3>
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '8px' }}>
          <BulletItem>Records your application date and target program so context is preserved.</BulletItem>
          <BulletItem>Sets new shifts you log to default to "waiting period" type so they're distinct from paid work.</BulletItem>
          <BulletItem>Tracks days since application, total hours logged, and care consistency.</BulletItem>
          <BulletItem>Displays a discreet amber banner in the app so you know the mode is active.</BulletItem>
          <BulletItem>Lets you generate a pre-assessment summary PDF to bring to your RN evaluation visit.</BulletItem>
        </ul>
      </div>

      <div style={{ padding: '10px 14px', background: '#FCF5E8', borderRadius: '8px', fontSize: '13px', color: '#8b5000', lineHeight: 1.6, display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <Info size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
        <div>
          <strong>Important:</strong> Documenting care during the waiting period does not guarantee compensation. Whether pre-approval care is compensable depends on program rules and your case. This mode helps you build the documentation you'd need if a retroactive claim becomes possible.
        </div>
      </div>
    </div>
  )
}

function Step2Date({ value, onChange }) {
  const today = new Date().toISOString().split('T')[0]

  return (
    <div>
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C', marginBottom: '6px' }}>
        When did you submit the application?
      </h2>
      <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6, marginBottom: '1.5rem' }}>
        Enter the date the Medicaid waiver application was submitted. This becomes the reference point for counting waiting period days. If you're not sure of the exact date, use your best estimate — you can update this later.
      </p>

      <Input
        label="Application submission date"
        id="wp-date"
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        max={today}
        required
      />

      {value && (
        <div style={{ padding: '10px 14px', background: '#E1F5EE', borderRadius: '8px', fontSize: '13px', color: '#005440', marginTop: '8px' }}>
          <Calendar size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          <strong>{daysSince(value)} days</strong> since application
        </div>
      )}
    </div>
  )
}

function Step3Program({ value, onChange }) {
  return (
    <div>
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C', marginBottom: '6px' }}>
        Which waiver program did you apply for?
      </h2>
      <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6, marginBottom: '1.5rem' }}>
        Select the Medicaid waiver program the application is for. This determines the eligibility criteria your care documentation should support.
      </p>

      <Select
        label="Program"
        id="wp-program"
        value={value}
        onChange={e => onChange(e.target.value)}
        options={PROGRAMS}
        required
      />

      {value && PROGRAM_DESCRIPTIONS[value] && (
        <div style={{ padding: '12px 14px', background: '#F7FAFC', borderRadius: '8px', fontSize: '13px', color: '#4A5568', lineHeight: 1.7, marginTop: '8px', borderLeft: '3px solid #2C7A7B' }}>
          <strong style={{ color: '#1A202C' }}>{PROGRAMS.find(p => p.value === value)?.label}</strong>
          <p style={{ marginTop: '4px' }}>{PROGRAM_DESCRIPTIONS[value]}</p>
        </div>
      )}
    </div>
  )
}

function Step4Confirm({ applicationDate, program }) {
  return (
    <div>
      <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 500, color: '#1A202C', marginBottom: '6px' }}>
        Ready to activate?
      </h2>
      <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6, marginBottom: '1.5rem' }}>
        Review the details below. Once activated, Waiting Period Mode will change how shifts are logged and add tracking to your dashboard. You can deactivate it at any time.
      </p>

      <Card style={{ background: '#F7FAFC', padding: '1rem 1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gap: '10px' }}>
          <ConfirmRow label="Application date" value={formatDate(applicationDate)} />
          <ConfirmRow label="Program" value={PROGRAMS.find(p => p.value === program)?.label} />
          <ConfirmRow label="Days elapsed" value={`${daysSince(applicationDate)} days`} />
          <ConfirmRow label="Mode" value="Waiting Period" />
        </div>
      </Card>

      <div style={{ padding: '10px 14px', background: '#FCF5E8', borderRadius: '8px', fontSize: '13px', color: '#8b5000', lineHeight: 1.6, display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <Info size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
        An amber banner will appear in the app to remind you that Waiting Period Mode is active. You can deactivate it any time from this page.
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════
// DASHBOARD (when mode is active)
// ═══════════════════════════════════════════════════════

function WaitingPeriodDashboard() {
  const { user, profile, refreshProfile } = useAuth()
  const { data: shifts } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: false })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id' })
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [toast, setToast] = useState(null)
  const [generating, setGenerating] = useState(false)

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r]))

  const stats = useMemo(() => {
    const appDate = new Date(profile.wp_application_date + 'T00:00:00')
    const now = new Date()
    const daysElapsed = Math.floor((now - appDate) / 86400000)

    // WP shifts = shifts logged since application date
    const wpShifts = shifts.filter(s => {
      if (!s.clock_out || s.is_active) return false
      return new Date(s.clock_in) >= appDate
    })

    const totalHours = wpShifts.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)

    // Days with care logged
    const daysWithCare = new Set(wpShifts.map(s => s.date)).size

    // Consistency: days with care / total days elapsed
    const consistency = daysElapsed > 0 ? Math.round((daysWithCare / daysElapsed) * 100) : 0

    // Task frequency
    const taskCounts = {}
    for (const s of wpShifts) {
      for (const t of (s.tasks || [])) {
        taskCounts[t] = (taskCounts[t] || 0) + 1
      }
    }
    const topTasks = Object.entries(taskCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)

    // Average hours per week
    const weeksElapsed = Math.max(daysElapsed / 7, 1)
    const avgHoursPerWeek = totalHours / weeksElapsed

    return {
      daysElapsed,
      totalHours,
      daysWithCare,
      consistency,
      shiftCount: wpShifts.length,
      topTasks,
      avgHoursPerWeek,
      wpShifts,
    }
  }, [shifts, profile])

  async function deactivate() {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ in_waiting_period: false })
        .eq('id', user.id)
      if (error) throw error
      setDeactivateOpen(false)
      refreshProfile()
      setToast({ message: 'Waiting Period Mode deactivated', type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  async function generatePreAssessmentPDF() {
    setGenerating(true)
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'letter' })
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 40

      // Header
      doc.setFillColor(0, 84, 64)
      doc.rect(0, 0, pageWidth, 90, 'F')
      doc.setFillColor(255, 255, 255)
      doc.roundedRect(margin, 25, 40, 40, 6, 6, 'F')
      doc.setTextColor(0, 84, 64)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(22)
      doc.text('K', margin + 20, 53, { align: 'center' })
      doc.setTextColor(255, 255, 255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(20)
      doc.text('KINDREDCARE', margin + 55, 45)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(11)
      doc.text('Pre-Assessment Care Summary', margin + 55, 63)
      doc.setFontSize(9)
      doc.text('CONFIDENTIAL', pageWidth - margin, 45, { align: 'right' })

      let y = 120

      // Purpose statement
      doc.setFillColor(252, 245, 232)
      doc.roundedRect(margin, y, pageWidth - 2 * margin, 50, 6, 6, 'F')
      doc.setTextColor(139, 80, 0)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text('PURPOSE', margin + 12, y + 18)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      const purposeText = 'This document summarizes care provided by the caregiver named below during the Medicaid waiver application waiting period. It is intended to support the RN level-of-care assessment and to establish a documented record of pre-approval care.'
      const purposeWrapped = doc.splitTextToSize(purposeText, pageWidth - 2 * margin - 24)
      doc.text(purposeWrapped, margin + 12, y + 32)
      y += 70

      // Caregiver and application info
      doc.setDrawColor(226, 232, 240)
      doc.line(margin, y, pageWidth - margin, y)
      y += 20

      const halfWidth = (pageWidth - 2 * margin) / 2
      doc.setTextColor(113, 128, 150)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text('CAREGIVER', margin, y)
      doc.text('APPLICATION SUBMITTED', margin + halfWidth, y)
      y += 14
      doc.setTextColor(26, 32, 44)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text(profile?.name || 'Unknown', margin, y)
      doc.text(formatDate(profile.wp_application_date), margin + halfWidth, y)

      y += 20
      doc.setTextColor(113, 128, 150)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text('WAIVER PROGRAM', margin, y)
      doc.text('DAYS ELAPSED', margin + halfWidth, y)
      y += 14
      doc.setTextColor(26, 32, 44)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      const programLabel = PROGRAMS.find(p => p.value === profile.wp_program)?.label || profile.wp_program.toUpperCase()
      doc.text(programLabel.split(' — ')[0], margin, y)
      doc.text(`${stats.daysElapsed} days`, margin + halfWidth, y)

      y += 30

      // Summary stats
      doc.line(margin, y, pageWidth - margin, y)
      y += 20
      doc.setTextColor(0, 84, 64)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.text('Care summary', margin, y)
      y += 18

      const statBoxWidth = (pageWidth - 2 * margin - 20) / 3
      const drawStat = (label, value, x, y) => {
        doc.setFillColor(247, 250, 252)
        doc.roundedRect(x, y, statBoxWidth, 50, 4, 4, 'F')
        doc.setTextColor(113, 128, 150)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.text(label.toUpperCase(), x + 10, y + 15)
        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(16)
        doc.text(String(value), x + 10, y + 38)
      }

      drawStat('Total hours', stats.totalHours.toFixed(1), margin, y)
      drawStat('Days with care', stats.daysWithCare, margin + statBoxWidth + 10, y)
      drawStat('Avg hrs/week', stats.avgHoursPerWeek.toFixed(1), margin + 2 * (statBoxWidth + 10), y)
      y += 60
      drawStat('Care consistency', `${stats.consistency}%`, margin, y)
      drawStat('Total shifts', stats.shiftCount, margin + statBoxWidth + 10, y)
      drawStat('Days elapsed', stats.daysElapsed, margin + 2 * (statBoxWidth + 10), y)
      y += 75

      // Top tasks
      if (stats.topTasks.length > 0) {
        doc.setTextColor(0, 84, 64)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(13)
        doc.text('Care tasks performed', margin, y)
        y += 5
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(8)
        doc.setTextColor(113, 128, 150)
        doc.text('Most frequent tasks documented during the waiting period.', margin, y + 10)
        y += 20

        const taskRows = stats.topTasks.map(([task, count]) => [
          task.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          `${count} shifts`,
          `${((count / stats.shiftCount) * 100).toFixed(0)}% of shifts`,
        ])

        autoTable(doc, {
          startY: y + 5,
          head: [['Task', 'Frequency', 'Percentage']],
          body: taskRows,
          theme: 'grid',
          headStyles: { fillColor: [0, 84, 64], textColor: 255, fontSize: 9, fontStyle: 'bold' },
          bodyStyles: { fontSize: 9, textColor: [74, 85, 104] },
          alternateRowStyles: { fillColor: [247, 250, 252] },
          margin: { left: margin, right: margin },
        })
        y = doc.lastAutoTable.finalY + 25
      }

      // Attestation
      if (y > pageHeight - 200) { doc.addPage(); y = margin }

      doc.setDrawColor(226, 232, 240)
      doc.line(margin, y, pageWidth - margin, y)
      y += 20

      doc.setTextColor(0, 84, 64)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.text('Attestation', margin, y)
      y += 18
      doc.setTextColor(74, 85, 104)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      const attestText = `I, ${profile.name}, attest that the care documented in this summary was provided by me to the applicant during the Medicaid waiver application waiting period beginning ${formatDate(profile.wp_application_date)}. The records were generated by KindredCare, a tamper-evident caregiver documentation platform. Shifts were logged at the time of care and lock 30 minutes after clock-out.`
      const attestWrapped = doc.splitTextToSize(attestText, pageWidth - 2 * margin)
      doc.text(attestWrapped, margin, y)
      y += attestWrapped.length * 12 + 30

      // Signature lines
      doc.setDrawColor(160, 174, 192)
      doc.line(margin, y, margin + 240, y)
      doc.line(margin + 280, y, margin + 400, y)
      y += 12
      doc.setTextColor(113, 128, 150)
      doc.setFontSize(9)
      doc.text('Caregiver signature', margin, y)
      doc.text('Date', margin + 280, y)

      // Footer
      const totalPages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        doc.setDrawColor(226, 232, 240)
        doc.line(margin, pageHeight - 50, pageWidth - margin, pageHeight - 50)
        doc.setTextColor(113, 128, 150)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(8)
        doc.text(
          'Generated by KindredCare · Tamper-evident caregiver documentation · kindredcare.io',
          margin,
          pageHeight - 30
        )
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 30, { align: 'right' })
      }

      doc.save(`kindredcare-pre-assessment-${new Date().toISOString().split('T')[0]}.pdf`)
      setToast({ message: 'Pre-assessment summary PDF downloaded', type: 'success' })
    } catch (err) {
      setToast({ message: `Error: ${err.message}`, type: 'error' })
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }}>
      <PageHeader
        title="Waiting Period Mode"
        description="Active — documenting pre-approval care while your waiver application is pending."
        action={<Badge variant="amber">Active since {formatDate(profile.wp_application_date)}</Badge>}
      />

      {/* Status card */}
      <Card style={{ background: '#FCF5E8', borderLeft: '3px solid #8b5000', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Clock size={20} style={{ color: '#8b5000', marginTop: '2px', flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#8b5000', marginBottom: '4px' }}>
              Waiting Period Mode is active
            </div>
            <div style={{ fontSize: '13px', color: '#8b5000', lineHeight: 1.6 }}>
              You applied for {PROGRAMS.find(p => p.value === profile.wp_program)?.label} on {formatDate(profile.wp_application_date)}. Shifts you log now default to "waiting period" type. Deactivate this mode once your case is approved and you begin receiving compensation.
            </div>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1.5rem' }}>
        <StatCard label="Days elapsed" value={stats.daysElapsed} sub="since application" />
        <StatCard label="Hours documented" value={stats.totalHours.toFixed(1)} sub={`${stats.shiftCount} shifts`} />
        <StatCard label="Days with care" value={stats.daysWithCare} sub={`${stats.consistency}% consistency`} color="#005440" />
        <StatCard label="Avg per week" value={`${stats.avgHoursPerWeek.toFixed(1)}h`} />
      </div>

      {/* Actions */}
      <h2 style={{ fontSize: '14px', fontWeight: 500, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
        Actions
      </h2>

      <div style={{ display: 'grid', gap: '10px', marginBottom: '1.5rem' }}>
        <Card style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <div style={{ padding: '10px', background: '#E1F5EE', borderRadius: '8px', flexShrink: 0 }}>
            <FileText size={20} style={{ color: '#005440' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
              Pre-assessment summary PDF
            </div>
            <div style={{ fontSize: '13px', color: '#4A5568', marginBottom: '10px', lineHeight: 1.5 }}>
              A branded PDF summarizing the care you've documented — tasks performed, hours per week, care consistency, and an attestation you can sign. Bring this to your RN level-of-care assessment visit.
            </div>
            <Button onClick={generatePreAssessmentPDF} disabled={generating || stats.shiftCount === 0}>
              <Download size={16} /> {generating ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>
        </Card>

        <Card style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <div style={{ padding: '10px', background: '#F7FAFC', borderRadius: '8px', flexShrink: 0 }}>
            <AlertTriangle size={20} style={{ color: '#4A5568' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 500, color: '#1A202C', marginBottom: '4px' }}>
              Deactivate Waiting Period Mode
            </div>
            <div style={{ fontSize: '13px', color: '#4A5568', marginBottom: '10px', lineHeight: 1.5 }}>
              Turn off Waiting Period Mode once your waiver is approved and you begin receiving compensation. Your existing waiting period shifts stay on your record permanently.
            </div>
            <Button variant="secondary" onClick={() => setDeactivateOpen(true)}>
              Deactivate mode
            </Button>
          </div>
        </Card>
      </div>

      {/* Deactivate confirmation */}
      <Modal open={deactivateOpen} onClose={() => setDeactivateOpen(false)} title="Deactivate Waiting Period Mode?" width={440}>
        <p style={{ color: '#4A5568', marginBottom: '1rem', lineHeight: 1.6 }}>
          This will turn off Waiting Period Mode. Your existing waiting period shifts and stats stay on your record permanently. New shifts will default to "direct care" type going forward.
        </p>
        <p style={{ color: '#8b5000', marginBottom: '1rem', lineHeight: 1.6, fontSize: '13px' }}>
          <strong>Recommended:</strong> Generate the pre-assessment PDF before deactivating, so you have a snapshot of the waiting period on file.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setDeactivateOpen(false)}>Cancel</Button>
          <Button variant="coral" onClick={deactivate}>Deactivate mode</Button>
        </div>
      </Modal>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function daysSince(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  const now = new Date()
  return Math.floor((now - date) / 86400000)
}

function BulletItem({ children }) {
  return (
    <li style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#4A5568', lineHeight: 1.6 }}>
      <Check size={14} style={{ color: '#0F6E56', marginTop: '2px', flexShrink: 0 }} />
      <span>{children}</span>
    </li>
  )
}

function ConfirmRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' }}>
      <span style={{ fontSize: '13px', color: '#718096', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '14px', color: '#1A202C', fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  )
}

const sectionTitleStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#4A5568',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '10px',
}
